import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, ChevronDown, MessageSquare } from "lucide-react";
import { categoriesMegaColumns, manufacturersMegaColumns } from "@/components/header/megaMenuData";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

interface Group {
  label: string;
  columns: { heading: string; items: { label: string; to: string }[] }[];
  footer: { label: string; to: string };
}

const MobileNav = ({ onClose }: { onClose: () => void }) => {
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const groups: Group[] = [
    {
      label: "Categories",
      columns: categoriesMegaColumns(),
      footer: { label: "View all categories", to: "/categories" },
    },
    {
      label: "Manufacturers",
      columns: manufacturersMegaColumns(),
      footer: { label: "View all manufacturers", to: "/manufacturers" },
    },
  ];

  const toggle = (label: string) =>
    setOpenGroup((cur) => (cur === label ? null : label));

  const linkCls =
    "flex items-center min-h-11 text-foreground/85 font-medium hover:text-brand transition-colors";

  return (
    <div className="max-h-[calc(100vh-4rem)] overflow-y-auto px-4 py-4 space-y-1">
      <Link to="/" onClick={onClose} className={linkCls}>Home</Link>
      <Link to="/shop" onClick={onClose} className={linkCls}>Shop All Products</Link>
      <Link to="/industrial-automation-parts" onClick={onClose} className={linkCls}>Automation Parts Hub</Link>
      <Link to="/industrial-automation-repair-services" onClick={onClose} className={linkCls}>Repair Services</Link>

      {groups.map((g) => {
        const isOpen = openGroup === g.label;
        return (
          <div key={g.label} className="border-t border-border first-of-type:mt-1">
            <button
              type="button"
              onClick={() => toggle(g.label)}
              aria-expanded={isOpen}
              aria-controls={`mobile-group-${g.label}`}
              className="w-full flex items-center justify-between min-h-11 text-foreground font-semibold"
            >
              <span>{g.label}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div id={`mobile-group-${g.label}`} className="pb-3 pl-1 space-y-3">
                {g.columns.map((col) => (
                  <div key={col.heading}>
                    <p className="text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 mt-2">
                      {col.heading}
                    </p>
                    <ul className="space-y-0.5">
                      {col.items.map((item) => (
                        <li key={item.to}>
                          <Link
                            to={item.to}
                            onClick={onClose}
                            className="block min-h-10 py-2 text-sm text-foreground/80 hover:text-brand transition-colors"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <Link
                  to={g.footer.to}
                  onClick={onClose}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all mt-1"
                >
                  {g.footer.label} →
                </Link>
              </div>
            )}
          </div>
        );
      })}

      <div className="border-t border-border pt-2">
        <Link to="/about" onClick={onClose} className={linkCls}>About Us</Link>
        <Link to="/contact" onClick={onClose} className={linkCls}>Contact Us</Link>
      </div>

      <div className="border-t border-border pt-3 mt-3 space-y-2 text-sm text-foreground/70">
        <a href="tel:+18454533201" className="flex items-center gap-2 min-h-11">
          <Phone className="w-4 h-4 text-brand" /> +1 (845) 453-3201
        </a>
        <a href="mailto:sales@nardyautomation.com" className="flex items-center gap-2 min-h-11">
          <Mail className="w-4 h-4 text-brand" /> <span translate="no">sales@nardyautomation.com</span>
        </a>
        <a
          href="https://wa.me/18454533201"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 min-h-11 hover:text-brand transition-colors"
        >
          <WhatsAppIcon className="w-4 h-4 text-[#25D366]" /> WhatsApp
        </a>
      </div>
    </div>
  );
};

export default MobileNav;